/*
 * The antenatal visit form conflated two different questions (KIN-2075):
 *
 *   which foetuses to ask about — the ones still continuing, for a NEW visit;
 *   which foetuses a visit recorded — whatever its own observations hold, however many
 *     have since ended.
 *
 * It asked only the first, from `pregnancy.foetuses` filtered on `isActive`. Correcting an
 * earlier visit therefore showed no Foetal Exam section for a foetus that has since ended, so
 * the findings recorded for it that day were invisible and could not be corrected — while the
 * read-only view of the very same visit showed them, because that renders from the
 * consultation's own observations.
 *
 * This answers the second question. It needs nothing new from the backend: each observation is
 * already enriched with its foetus through the unfiltered `getPregnancyFoetus(id)`, so an ended
 * foetus arrives complete with its `isActive: false` and its outcome.
 */

const byIdentifier = (a, b) =>
  String(a.foetusIdentifier ?? "").localeCompare(
    String(b.foetusIdentifier ?? "")
  );

const isSameFoetus = (a, b) =>
  (a?.id != null && a.id === b?.id) ||
  (!!a?.foetusIdentifier && a.foetusIdentifier === b?.foetusIdentifier);

/**
 * The foetuses a visit's form has to show: the ones still continuing, plus any this visit
 * already recorded against. Ordered by identifier so Baby B never appears above Baby A.
 *
 * On a new visit there are no observations yet, so the answer is exactly the live list and
 * nothing changes. An ended foetus comes back carrying `isActive: false`, which is what tells
 * the form to render its section read-only rather than inviting changes to a baby that is no
 * longer continuing.
 */
export const resolveConsultationFoetuses = ({
  liveFoetuses = [],
  observations = [],
} = {}) => {
  const recorded = (observations ?? [])
    .map((observation) => observation?.pregnancyFoetus)
    .filter(Boolean);

  const extras = recorded.filter(
    (foetus, index) =>
      !(liveFoetuses ?? []).some((live) => isSameFoetus(live, foetus)) &&
      // The same foetus can hold more than one observation on a visit, so only its first
      // appearance counts — compared by position, because two observations may well carry
      // two equal-but-separate copies of the same foetus.
      recorded.findIndex((other) => isSameFoetus(other, foetus)) === index
  );

  return [...(liveFoetuses ?? []), ...extras].sort(byIdentifier);
};

/**
 * A foetus that did not continue. Absent `isActive` means a live one: the live list itself is
 * already filtered on it, and a foetus the backend sent without the flag is not evidence that
 * it ended.
 */
export const isEndedFoetus = (foetus) => foetus?.isActive === false;
